import React, { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { RadixButton, RadixDatePicker, RadixDialog, RadixSelect } from '@/components/radix';
import { useMediaQuery } from '@/hooks/shared';
import { GetApiDoctorsPortalLogsSortBy, GetApiDoctorsPortalLogsSortOrder } from '@/shared/api/models';

export interface LogsViewState {
    doctorId: string;
    sortBy?: string;
    sortOrder?: string;
    fromDate?: string;
    toDate?: string;
}

export interface LogsFilterModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    value: LogsViewState;
    onApply: (state: LogsViewState) => void;
    doctorOptions: Array<{ value: string; label: string }>;
}

const SORT_BY_NONE = '__none__';

export default function LogsFilterModal({ open, onOpenChange, value, onApply, doctorOptions }: LogsFilterModalProps) {
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const [draft, setDraft] = useState<LogsViewState>(value);

    useEffect(() => {
        if (open) setDraft({ ...value });
    }, [open, value]);

    const sortByOptions = useMemo(
        () => [
            { value: SORT_BY_NONE, label: t('Doctors.FilterSortByNone') },
            { value: GetApiDoctorsPortalLogsSortBy.createdAt, label: t('Doctors.LogsSortByCreatedAt') },
            { value: GetApiDoctorsPortalLogsSortBy.seqId, label: t('Doctors.LogsSortBySeqId') },
        ],
        [],
    );

    const sortOrderOptions = useMemo(
        () => [
            { value: GetApiDoctorsPortalLogsSortOrder.asc, label: t('Doctors.FilterSortOrderAsc') },
            { value: GetApiDoctorsPortalLogsSortOrder.desc, label: t('Doctors.FilterSortOrderDesc') },
        ],
        [],
    );

    const handleDone = () => {
        onApply(draft);
        onOpenChange(false);
    };

    const fromDateValue = draft.fromDate ? moment(draft.fromDate, 'YYYY-MM-DD', true).toDate() : null;
    const toDateValue = draft.toDate ? moment(draft.toDate, 'YYYY-MM-DD', true).toDate() : null;

    return (
        <RadixDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('Doctors.FilterAndSortTitle')}
            position={isDesktop ? 'center' : 'bottom'}
            footer={
                <div className="flex justify-end">
                    <RadixButton variant="primary" size="base" onClick={handleDone}>
                        {t('Doctors.FilterDone')}
                    </RadixButton>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.LogsFilterDoctor')}
                    </label>
                    <RadixSelect
                        value={draft.doctorId}
                        onValueChange={(v) => setDraft((s) => ({ ...s, doctorId: v }))}
                        options={doctorOptions}
                        placeholder={t('Doctors.LogsFilterDoctor')}
                        className="w-full"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.FilterSortBy')}
                    </label>
                    <RadixSelect
                        value={draft.sortBy ?? SORT_BY_NONE}
                        onValueChange={(v) => setDraft((s) => ({ ...s, sortBy: v === SORT_BY_NONE ? undefined : v }))}
                        options={sortByOptions}
                        className="w-full"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.FilterSortOrder')}
                    </label>
                    <RadixSelect
                        value={draft.sortOrder ?? GetApiDoctorsPortalLogsSortOrder.desc}
                        onValueChange={(v) => setDraft((s) => ({ ...s, sortOrder: v || undefined }))}
                        options={sortOrderOptions}
                        className="w-full"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <RadixDatePicker
                        label={t('Doctors.LogsFromDate')}
                        value={fromDateValue}
                        onChange={(d) =>
                            setDraft((s) => ({ ...s, fromDate: d ? moment(d).format('YYYY-MM-DD') : undefined }))
                        }
                        placeholder={t('Doctors.LogsFromDate')}
                        format="DD/MM-YYYY"
                    />
                    <RadixDatePicker
                        label={t('Doctors.LogsToDate')}
                        value={toDateValue}
                        onChange={(d) =>
                            setDraft((s) => ({ ...s, toDate: d ? moment(d).format('YYYY-MM-DD') : undefined }))
                        }
                        placeholder={t('Doctors.LogsToDate')}
                        format="DD/MM-YYYY"
                    />
                </div>
            </div>
        </RadixDialog>
    );
}

